import React, { useEffect, useRef } from "react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { TooltipProvider } from "@/components/ui/tooltip";
import { INPUT_BOX_CLASS, INPUT_BOX_STYLE } from "./checkerFieldStyles";
import { GROUP_COMPANY_OPTIONS } from "./groupCompanyDisplay";
import { ChoiceCard, ChoiceGroup } from "./ChoiceCard";
import FieldError from "./FieldError";
import { fieldAnchorProps, invalidInputProps } from "./checkerValidation";
import {
  formatLatestFyDdMmYyyyInput,
  isoDateToDdMmYyyy,
} from "@/lib/latestFyDateInput";

const industries = [
  "Manufacturing & Engineering", "Technology & Software", "Healthcare & Life Sciences", 
  "Financial Services", "Professional Services", "Construction & Real Estate", 
  "Logistics & Supply Chain", "Education & Training", "Retail & E-commerce", 
  "Food & Beverage", "Energy & Sustainability", "Creative & Media", "Other"
];

const entityTypes = [
  "Private Limited",
  "Sole Proprietorship",
  "Partnership",
  "LLP",
  "Branch Office",
  "Subsidiary",
  "Other"
];

const groupRelationshipOptions = [
  "Parent company",
  "Subsidiary",
  "Sister company within group",
  "Branch office",
  "Not sure"
];

// const headcountOptions = [
//   "1-10", "11-50", "51-200", "201-500", "500+"
// ];

// const revenueOptions = [
//   "Under $1M", "$1M-$5M", "$5M-$10M", "$10M-$50M", "$50M-$100M", "Over $100M"
// ];

const _grantOptions = [
  "Productivity Solutions Grant (PSG)",
  "Enterprise Development Grant (EDG)",
  "Market Readiness Assistance (MRA)",
  "Enterprise Innovation Scheme (EIS)",
  "Skills Framework Enhancement Grant (SFEG)",
  "None previously received"
];

const _businessStageOptions = [
  "Startup",
  "Growth",
  "Mature",
  "Established"
];

export default function CompanyStep({ formData, updateFormData, errors = {} }) {
  const didNormalizeLegacyIsoFy = useRef(false);

  // One-time: convert yyyy-mm-dd from former <input type="date"> to dd/mm/yyyy
  useEffect(() => {
    if (didNormalizeLegacyIsoFy.current) return;
    const v = String(formData.latest_fy_end_date || "").trim();
    if (!/^\d{4}-\d{2}-\d{2}$/.test(v)) return;
    didNormalizeLegacyIsoFy.current = true;
    updateFormData({ latest_fy_end_date: isoDateToDdMmYyyy(v) });
  }, [formData.latest_fy_end_date, updateFormData]);
  const _handleGrantChange = (grantName, checked) => {
    const currentGrants = formData.past_grants || [];
    if (checked) {
      if (grantName === "None previously received") {
        updateFormData({ past_grants: [grantName] });
      } else {
        const filteredGrants = currentGrants.filter(g => g !== "None previously received");
        updateFormData({ past_grants: [...filteredGrants, grantName] });
      }
    } else {
      updateFormData({ past_grants: currentGrants.filter(g => g !== grantName) });
    }
  };

  const groupCompanyAnswered = ["yes", "no", "not_sure"].includes(
    formData.is_group_company
  );

  return (
    <TooltipProvider>
      <div id="step-company" className="space-y-12">
        {/* Header */}
        <div className="text-center mb-10">
          <h2 className="text-3xl font-bold mb-3" style={{ color: '#4B5563' }} tabIndex={-1} data-step-heading>Company Profile</h2>
          <p className="text-lg max-w-2xl mx-auto" style={{ color: '#4B5563' }}>
            Help us understand your business structure to provide accurate grant eligibility assessment
          </p>
        </div>

        {/* Form Fields */}
        <div className="space-y-10">
          {/* Company Information Section */}
          <div className="w-full rounded-3xl bg-white/5 backdrop-blur-xl shadow-2xl border border-white/10 p-6 md:p-8">
            <h3 className="mb-6 text-xl font-semibold" style={{ color: '#4B5563' }}>Company Information</h3>
            <div className="grid md:grid-cols-2 gap-6">
              <div className="space-y-3" {...fieldAnchorProps('companyName')}>
                <Label htmlFor="companyName" className="font-medium" style={{ color: '#4B5563' }}>
                  Company Name <span style={{ color: '#4B5563' }}>*</span>
                </Label>
                <Input
                  id="companyName"
                  name="companyName"
                  type="text"
                  placeholder="e.g., ABC Pte Ltd"
                  value={formData.companyName || ''}
                  onChange={(e) => updateFormData({ companyName: e.target.value })}
                  required
                  style={INPUT_BOX_STYLE}
                  className={INPUT_BOX_CLASS}
                  {...invalidInputProps('companyName', errors.companyName)}
                />
                <FieldError name="companyName" message={errors.companyName} />
              </div>

              <div className="space-y-3" {...fieldAnchorProps('uen')}>
                <Label htmlFor="uen" className="font-medium" style={{ color: '#4B5563' }}>
                  UEN <span style={{ color: '#4B5563' }}>*</span>
                </Label>
                <Input
                  id="uen"
                  name="uen"
                  type="text"
                  autoComplete="off"
                  placeholder="e.g., 202012345A"
                  value={formData.uen || ''}
                  onChange={(e) => updateFormData({ uen: e.target.value.toUpperCase() })}
                  required
                  style={INPUT_BOX_STYLE}
                  className={INPUT_BOX_CLASS}
                  {...invalidInputProps('uen', errors.uen, 'uen-hint')}
                />
                <p id="uen-hint" className="text-xs" style={{ color: '#6B7280' }}>
                  Unique Entity Number as shown on your ACRA BizFile profile (9 or 10 characters, e.g. 202012345A or 53012345A).
                </p>
                <FieldError name="uen" message={errors.uen} />
              </div>

              <div className="space-y-3" {...fieldAnchorProps('incorporation_year')}>
                <Label htmlFor="incorporation" className="font-medium" style={{ color: '#4B5563' }}>
                  Year of Incorporation <span style={{ color: '#4B5563' }}>*</span>
                </Label>
                <Input
                  id="incorporation"
                  name="incorporation_year"
                  type="number"
                  min="1960"
                  max={new Date().getFullYear()}
                  placeholder="e.g., 2020"
                  value={formData.incorporation_year || ''}
                  onChange={(e) => updateFormData({ incorporation_year: e.target.value === '' ? '' : parseInt(e.target.value) })}
                  required
                  style={INPUT_BOX_STYLE}
                  className={INPUT_BOX_CLASS}
                  {...invalidInputProps('incorporation_year', errors.incorporation_year)}
                />
                <FieldError name="incorporation_year" message={errors.incorporation_year} />
              </div>

              <div className="space-y-3" {...fieldAnchorProps('industry')}>
                <Label id="industry-label" htmlFor="industry" className="font-medium" style={{ color: '#4B5563' }}>
                  Primary Industry <span style={{ color: '#4B5563' }}>*</span>
                </Label>
                <Select value={formData.industry || ''} onValueChange={(value) => updateFormData({ industry: value })}>
                  <SelectTrigger
                    id="industry"
                    aria-labelledby="industry-label"
                    className={INPUT_BOX_CLASS}
                    style={INPUT_BOX_STYLE}
                    {...invalidInputProps('industry', errors.industry)}
                  >
                    <SelectValue placeholder="Select your primary industry" />
                  </SelectTrigger>
                  <SelectContent>
                    {industries.map(ind => (
                      <SelectItem key={ind} value={ind}>{ind}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError name="industry" message={errors.industry} />
              </div>

              <div className="space-y-3" {...fieldAnchorProps('entity_type')}>
                <Label id="entity_type-label" htmlFor="entity_type" className="font-medium" style={{ color: '#4B5563' }}>
                  Business Entity Type <span style={{ color: '#4B5563' }}>*</span>
                </Label>
                <Select
                  value={formData.entity_type || ''}
                  onValueChange={(value) => updateFormData({ entity_type: value })}
                >
                  <SelectTrigger
                    id="entity_type"
                    aria-labelledby="entity_type-label"
                    className={INPUT_BOX_CLASS}
                    style={INPUT_BOX_STYLE}
                    {...invalidInputProps('entity_type', errors.entity_type)}
                  >
                    <SelectValue placeholder="Select your entity type" />
                  </SelectTrigger>
                  <SelectContent>
                    {entityTypes.map((t) => (
                      <SelectItem key={t} value={t}>{t}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <FieldError name="entity_type" message={errors.entity_type} />
              </div>
            </div>

            {/* Group / parent company structure */}
            <div className="mt-8 space-y-6">
              <ChoiceGroup
                name="is_group_company"
                legend="Group / Parent Company Relationship"
                required
                hint="Is your company part of a group or owned by a parent company?"
                error={errors.is_group_company}
              >
                {GROUP_COMPANY_OPTIONS.map((opt) => (
                  <ChoiceCard
                    key={opt.value}
                    name="is_group_company"
                    value={opt.value}
                    checked={formData.is_group_company === opt.value}
                    groupAnswered={groupCompanyAnswered}
                    onChange={(value) => updateFormData({ is_group_company: value })}
                    title={opt.label}
                  />
                ))}
              </ChoiceGroup>

              {formData.is_group_company === 'yes' && (
                <div className="space-y-3" {...fieldAnchorProps('group_relationship')}>
                  <Label id="group_relationship-label" htmlFor="group_relationship" className="font-medium" style={{ color: '#4B5563' }}>
                    Relationship Details <span style={{ color: '#4B5563' }}>*</span>
                  </Label>
                  <Select
                    value={formData.group_relationship || ''}
                    onValueChange={(value) => updateFormData({ group_relationship: value })}
                  >
                    <SelectTrigger
                      id="group_relationship"
                      aria-labelledby="group_relationship-label"
                      className={INPUT_BOX_CLASS}
                      style={INPUT_BOX_STYLE}
                      {...invalidInputProps('group_relationship', errors.group_relationship)}
                    >
                      <SelectValue placeholder="Select relationship" />
                    </SelectTrigger>
                    <SelectContent>
                      {groupRelationshipOptions.map((opt) => (
                        <SelectItem key={opt} value={opt}>{opt}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <FieldError name="group_relationship" message={errors.group_relationship} />
                  <Label htmlFor="parentName" className="font-medium" style={{ color: '#4B5563' }}>
                    Parent / Group Company Name (optional)
                  </Label>
                  <Input
                    id="parentName"
                    type="text"
                    placeholder="e.g., ABC Holdings Pte Ltd"
                    value={formData.parent_company_name || ''}
                    onChange={(e) => updateFormData({ parent_company_name: e.target.value })}
                    style={INPUT_BOX_STYLE}
                    className={INPUT_BOX_CLASS}
                  />
                </div>
              )}
            </div>

            {/* Latest financial year end */}
            <div className="mt-8 space-y-3" {...fieldAnchorProps('latest_fy_end_date')}>
              <Label htmlFor="latest_fy_end_date" className="font-medium" style={{ color: '#4B5563' }}>
                Latest Completed Financial Year / Audited Period End <span style={{ color: '#4B5563' }}>*</span>
              </Label>
              <Input
                id="latest_fy_end_date"
                name="latest_fy_end_date"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                placeholder="dd/mm/yyyy"
                value={formData.latest_fy_end_date || ""}
                onChange={(e) =>
                  updateFormData({
                    latest_fy_end_date: formatLatestFyDdMmYyyyInput(
                      e.target.value
                    ),
                  })}
                style={INPUT_BOX_STYLE}
                className={INPUT_BOX_CLASS}
                {...invalidInputProps('latest_fy_end_date', errors.latest_fy_end_date, 'latest_fy_end_date-hint')}
              />
              <p id="latest_fy_end_date-hint" className="text-xs" style={{ color: "#6B7280" }}>
                Enter as dd/mm/yyyy (e.g. 31/12/2024).
              </p>
              <FieldError name="latest_fy_end_date" message={errors.latest_fy_end_date} />
            </div>
          </div>

        </div>
      </div>
    </TooltipProvider>
  );
}
